import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useScript } from '../context/ScriptContext';
import { Script } from '../utils/transliterate';
import * as api from '../services/api';
import { Category } from '../types';

/* Small decorative flower/rosette ornament used as nav separator */
const NavOrnament: React.FC = () => (
  <span className="nav-sep" aria-hidden="true">
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* 6-petal flower rosette */}
      <ellipse cx="12" cy="7" rx="2.8" ry="5" fill="currentColor" opacity="0.6" />
      <ellipse cx="12" cy="17" rx="2.8" ry="5" fill="currentColor" opacity="0.6" />
      <ellipse cx="7" cy="9.5" rx="2.8" ry="5" fill="currentColor" opacity="0.6" transform="rotate(60 7 9.5)" />
      <ellipse cx="17" cy="14.5" rx="2.8" ry="5" fill="currentColor" opacity="0.6" transform="rotate(60 17 14.5)" />
      <ellipse cx="7" cy="14.5" rx="2.8" ry="5" fill="currentColor" opacity="0.6" transform="rotate(-60 7 14.5)" />
      <ellipse cx="17" cy="9.5" rx="2.8" ry="5" fill="currentColor" opacity="0.6" transform="rotate(-60 17 9.5)" />
      <circle cx="12" cy="12" r="2.5" fill="currentColor" opacity="0.8" />
    </svg>
  </span>
);

const LANG_OPTIONS: { code: Script; flag: string; label: string }[] = [
  { code: 'cyr', flag: '🇷🇸', label: 'ЋИР' },
  { code: 'lat', flag: '🇷🇸', label: 'LAT' },
  { code: 'en', flag: '🇬🇧', label: 'EN' },
  { code: 'de', flag: '🇩🇪', label: 'DE' },
  { code: 'fr', flag: '🇫🇷', label: 'FR' },
  { code: 'it', flag: '🇮🇹', label: 'IT' },
  { code: 'el', flag: '🇬🇷', label: 'ΕΛ' },
];

interface NavbarProps {
  hideProcurements?: boolean;
  hideUsefulTexts?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ hideProcurements, hideUsefulTexts }) => {
  const { script, setScript, t } = useScript();
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const langRef = useRef<HTMLDivElement>(null);

  // Close language dropdown when clicking/touching outside (desktop only)
  useEffect(() => {
    const handler = (e: MouseEvent | TouchEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, []);

  useEffect(() => {
    api.getCategories().then(data => {
      setCategories(data.filter(c => c.parentId === null));
    }).catch(console.error);
  }, []);

  const currentLang = LANG_OPTIONS.find(l => l.code === script) || LANG_OPTIONS[0];

  const selectLang = (code: Script) => {
    setScript(code);
    setLangOpen(false);
    setMenuOpen(false);
  };

  /* Desktop language dropdown — unchanged design */
  const langDropdown = () => (
    <div className="lang-selector" ref={langRef}>
      <button
        className="lang-current"
        onClick={() => setLangOpen(!langOpen)}
        aria-haspopup="listbox"
        aria-expanded={langOpen}
        aria-label="Select language"
      >
        {/* Globe icon */}
        <svg className="lang-globe-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        <span className="lang-flag">{currentLang.flag}</span>
        <span className="lang-code">{currentLang.label}</span>
        <span className="lang-arrow">{langOpen ? '▴' : '▾'}</span>
      </button>
      {langOpen && (
        <ul className="lang-dropdown" role="listbox" aria-label="Languages">
          {LANG_OPTIONS.map(opt => (
            <li key={opt.code} role="option" aria-selected={script === opt.code}>
              <button
                className={`lang-option ${script === opt.code ? 'lang-option-active' : ''}`}
                onClick={() => selectLang(opt.code)}
              >
                <span className="lang-flag">{opt.flag}</span>
                <span>{opt.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  /* Mobile language grid — flat list, no dropdown needed. Eliminates all
     z-index / overflow / touch-event issues that broke the dropdown on phones. */
  const mobileLangGrid = () => (
    <div className="mobile-lang-grid" role="listbox" aria-label="Select language">
      {LANG_OPTIONS.map(opt => (
        <button
          key={opt.code}
          className={`mobile-lang-btn ${script === opt.code ? 'mobile-lang-btn-active' : ''}`}
          role="option"
          aria-selected={script === opt.code}
          onClick={() => selectLang(opt.code)}
        >
          <span className="lang-flag">{opt.flag}</span>
          <span>{opt.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <>
      <nav className="navbar">
        <div className="nav-container">
          <Link to="/" className="brand">
            <img
              src="/resource/logo/3.png"
              alt="Манастир Градац"
              className="brand-logo"
            />
          </Link>

          {/* Desktop nav — centered between logo and lang */}
          <div className="nav-links nav-links-compact">
            {categories
              .filter(cat => !(hideUsefulTexts && cat.slug === 'korisni-tekstovi'))
              .map((cat, i) => (
              <React.Fragment key={cat.id}>
                <Link to={`/${cat.slug}`} className="nav-novosti-link">
                  {t(cat.title)}
                </Link>
                <NavOrnament />
              </React.Fragment>
            ))}
            <Link to="/galerija" className="nav-novosti-link">{t('Галерија')}</Link>
            <NavOrnament />
            <Link to="/novosti" className="nav-novosti-link">{t('Новости')}</Link>
            {!hideProcurements && (
              <>
                <NavOrnament />
                <Link to="/isporuke" className="nav-novosti-link">{t('Јавне набавке')}</Link>
              </>
            )}
          </div>

          {/* Desktop language selector — always right */}
          <div className="nav-lang-right">
            {langDropdown()}
          </div>

          {/* Hamburger (mobile) */}
          <button
            className={`hamburger ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Meni"
            aria-expanded={menuOpen}
          >
            <span /><span /><span />
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <>
            <div
              className="mobile-menu-backdrop"
              onClick={() => setMenuOpen(false)}
              aria-hidden="true"
            />
            <div className="mobile-menu">
              {/* Language selector at the top — flat grid, always visible */}
              <div className="mobile-lang-section">
                {mobileLangGrid()}
              </div>
              {categories
                .filter(cat => !(hideUsefulTexts && cat.slug === 'korisni-tekstovi'))
                .map(cat => (
                <Link
                  key={cat.id}
                  to={`/${cat.slug}`}
                  className="nav-novosti-link mobile-link-item"
                  onClick={() => setMenuOpen(false)}
                >
                  {t(cat.title)}
                </Link>
              ))}
              <Link
                to="/galerija"
                className="nav-novosti-link mobile-link-item"
                onClick={() => setMenuOpen(false)}
              >{t('Галерија')}</Link>
              <Link
                to="/novosti"
                className="nav-novosti-link mobile-link-item"
                onClick={() => setMenuOpen(false)}
              >{t('Новости')}</Link>
              {!hideProcurements && (
                <Link
                  to="/isporuke"
                  className="nav-novosti-link mobile-link-item"
                  onClick={() => setMenuOpen(false)}
                >{t('Јавне набавке')}</Link>
              )}
            </div>
          </>
        )}
      </nav>
    </>
  );
};

export default Navbar;
